import moment, { max } from "moment/moment";
import { ReportType } from "PFApp/booking/types/export_booking";
import { PAST_DATE_KEYS } from "PFComponents/calendar/parts/calendar_periods/calendar_periods_constants";
import { usePeriods } from "PFCore/helpers/use_periods";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { PeriodRange, Value } from "PFTypes";
import { useMemo } from "react";

import { useExportWizardModalContext } from "../../../export_wizard_modal_provider";
import { useReportType } from "../../use_report_type";
import { findPeriodFilter } from "./helpers";

type PeriodOption = {
  id: string;
  item: {
    start: string;
    end: string;
  };
  displayElement: string;
  subtitle: string;
  alternativeStyling: boolean;
  noAction: boolean;
  selected: boolean;
};

type UsePeriodSelectorReturn = {
  minDate: string;
  options: PeriodOption[];
};

export const usePeriodSelector = (): UsePeriodSelectorReturn => {
  const {
    state: {
      data: { selectedFilters }
    }
  } = useExportWizardModalContext();
  const { reportType } = useReportType();
  const { formatISODate } = useDateFormatter();
  const periodsData = usePeriods();

  const selectedPeriod = useMemo(
    () => findPeriodFilter<Value>(selectedFilters) as PeriodRange,
    [selectedFilters]
  );

  const availablePeriodsData = useMemo(() => {
    if (reportType !== ReportType.Profiles) {
      return periodsData;
    }
    return Object.fromEntries(Object.entries(periodsData).filter(([key]) => !PAST_DATE_KEYS.includes(key)));
  }, [periodsData, reportType]);

  const options = useMemo(
    () =>
      Object.entries(availablePeriodsData).map(([key, value]) => {
        const adjustedStartDate =
          reportType === ReportType.Profiles ? max(value.range.startDate, moment()) : value.range.startDate;
        return {
          id: key,
          item: {
            start: formatISODate(adjustedStartDate),
            end: formatISODate(value.range.endDate)
          },
          displayElement: value.header,
          subtitle: value.label,
          alternativeStyling: true,
          noAction: key === "manual",
          selected: key === selectedPeriod?.period || (key === "manual" && !selectedPeriod?.period)
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPeriod, availablePeriodsData, reportType]
  );

  const minDate = useMemo(() => {
    if (reportType === ReportType.Profiles) {
      return formatISODate(moment());
    }
    return formatISODate(periodsData.lastYear.range.startDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, periodsData.lastYear.range.startDate]);

  return { minDate, options };
};
